// src/pages/Employees/components/GratuityCalculatorModal.jsx
import React, { useState } from "react";
import { Modal, DatePicker, InputNumber, Radio, Button, Typography, Table, Alert } from "antd";
import dayjs from "dayjs";
import { gratuityCalculatorApi } from "../../../apiservice/hrpayroll";

const { Text } = Typography;

const formatCurrency = (amount, currency) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "AED",
    minimumFractionDigits: 2,
  }).format(Number(amount || 0));

// Standalone, employee-agnostic gratuity calculator — lets HR manually
// verify a number (different salary, different dates) without it being
// tied to a real employee record. Same calculation engine as the real
// Final Settlement and the per-employee estimate card.
const GratuityCalculatorModal = ({ open, onClose, isIndia, currency }) => {
  const [basicSalary, setBasicSalary] = useState(null);
  const [joiningDate, setJoiningDate] = useState(null);
  const [asOfDate, setAsOfDate] = useState(dayjs());
  const [employeeType, setEmployeeType] = useState("permanent");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCalculate = async () => {
    if (!basicSalary || !joiningDate || !asOfDate) {
      setError("Please fill in Basic Salary, Joining Date, and As-of Date.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await gratuityCalculatorApi({
        basic_salary: basicSalary,
        joining_date: joiningDate.format("YYYY-MM-DD"),
        as_of_date: asOfDate.format("YYYY-MM-DD"),
        employee_type: employeeType,
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to calculate.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setBasicSalary(null);
    setJoiningDate(null);
    setAsOfDate(dayjs());
    setEmployeeType("permanent");
    setResult(null);
    setError(null);
    onClose();
  };

  const breakdownColumns = [
    { title: "From", dataIndex: "from" },
    { title: "To", dataIndex: "to" },
    { title: "Days", dataIndex: "days", align: "center" },
    { title: "Amount", dataIndex: "amount", align: "right", render: (v) => formatCurrency(v, currency) },
  ];

  return (
    <Modal
      title="End of Service Gratuity — Manual Calculator"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={520}
    >
      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 16 }}>
        For manual verification only — not tied to any employee record. Uses the {isIndia ? "Payment of Gratuity Act 1972" : "UAE Federal Decree-Law 33/2021 (Article 51)"} formula.
      </Text>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>Basic Salary</div>
        <InputNumber
          value={basicSalary}
          onChange={setBasicSalary}
          min={0}
          style={{ width: "100%" }}
          addonBefore={currency}
        />
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>Joining Date</div>
          <DatePicker value={joiningDate} onChange={setJoiningDate} format="DD-MMM-YYYY" style={{ width: "100%" }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>As-of / Last Working Day</div>
          <DatePicker value={asOfDate} onChange={setAsOfDate} format="DD-MMM-YYYY" style={{ width: "100%" }} />
        </div>
      </div>

      {isIndia && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>Employment Type</div>
          <Radio.Group value={employeeType} onChange={(e) => setEmployeeType(e.target.value)}>
            <Radio value="permanent">Permanent (5-year threshold)</Radio>
            <Radio value="temporary">Fixed-term Contract (1-year threshold)</Radio>
          </Radio.Group>
        </div>
      )}

      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

      <Button type="primary" loading={loading} onClick={handleCalculate} block>
        Calculate
      </Button>

      {result && (
        <div
          style={{
            marginTop: 16,
            border: "1px solid #f0f0f0",
            borderTop: "3px solid #722ed1",
            borderRadius: 8,
            padding: 16,
          }}
        >
          {result.eligible ? (
            <>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#722ed1" }}>
                {formatCurrency(result.amount, currency)}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {result.completedYears} completed year(s) of service
              </Text>
              {result.capped && (
                <Alert type="warning" showIcon message="Capped at the statutory maximum (2 years' basic salary)." style={{ marginTop: 8 }} />
              )}
              {result.exceedsCeiling && (
                <Alert type="warning" showIcon message="Exceeds the ₹20 lakh tax-exemption ceiling — amount above this is taxable." style={{ marginTop: 8 }} />
              )}
              {result.breakdown?.length > 0 && (
                <div style={{ marginTop: 12, overflowX: "auto" }}>
                  <Table size="small" rowKey={(r, i) => i} columns={breakdownColumns} dataSource={result.breakdown} pagination={false} />
                </div>
              )}
            </>
          ) : (
            <Text type="secondary">
              Not yet eligible
              {result.yearsUntilEligible != null && ` — ${result.yearsUntilEligible} more year(s) of service required`}.
            </Text>
          )}
        </div>
      )}
    </Modal>
  );
};

export default GratuityCalculatorModal;
